export type MessageVariableKey =
	| 'first_name'
	| 'job_name'
	| 'stage_name'
	| 'candidate_name'
	| 'workspace_name'
	| 'sender_first_name'
	| 'sender_name'
	| 'assignment_link';

export type MessageVariable = {
	id: MessageVariableKey;
	token: string;
	label: string;
	description?: string;
	sampleValue?: string;
};

export const MESSAGE_VARIABLES: MessageVariable[] = [
	{
		id: 'first_name',
		token: '{first_name}',
		label: 'first_name',
		description: 'Candidate first name',
	},
];

/** Expanded by the backend like `{first_name}`, but only when the bulk-message request carries
 *  `assignment_id` — each recipient gets their own link. Kept out of MESSAGE_VARIABLES so only the
 *  assignment composer offers it. */
export const ASSIGNMENT_LINK_VARIABLE: MessageVariable = {
	id: 'assignment_link',
	token: '{assignment_link}',
	label: 'assignment_link',
	description: 'Link to the assignment',
};

// Workspace config-default templates (subject line, interview name). Kept out of
// MESSAGE_VARIABLES so the chat composer's variable dropdown is unaffected.
export const TEMPLATE_VARIABLES = {
	jobName: {
		id: 'job_name',
		token: '{job_name}',
		label: 'job_name',
		description: 'Job name',
	},
	stageName: {
		id: 'stage_name',
		token: '{stage_name}',
		label: 'stage_name',
		description: 'Candidate current stage',
	},
	candidateName: {
		id: 'candidate_name',
		token: '{candidate_name}',
		label: 'candidate_name',
		description: 'Candidate full name',
	},
	workspaceName: {
		id: 'workspace_name',
		token: '{workspace_name}',
		label: 'workspace_name',
		description: 'Workspace name',
	},
} as const satisfies Record<string, MessageVariable>;

export const SUBJECT_LINE_VARIABLES: MessageVariable[] = [
	TEMPLATE_VARIABLES.jobName,
	TEMPLATE_VARIABLES.candidateName,
	TEMPLATE_VARIABLES.workspaceName,
];

export const SENDER_NAME_VARIABLE: MessageVariable = {
	id: 'sender_name',
	token: '{sender_name}',
	label: 'sender_name',
	description: 'Your full name',
};

export const SENDER_FIRST_NAME_VARIABLE: MessageVariable = {
	id: 'sender_first_name',
	token: '{sender_first_name}',
	label: 'sender_first_name',
	description: 'Your first name',
};

/** Filled by the backend for bulk sends and by the app for one-to-one sends. */
export const BUILT_IN_MESSAGE_VARIABLES: MessageVariable[] = [
	...MESSAGE_VARIABLES,
	TEMPLATE_VARIABLES.jobName,
	SENDER_FIRST_NAME_VARIABLE,
	SENDER_NAME_VARIABLE,
];

/** Only offered where the backend can expand the link — it does so only when the request carries `assignment_id`. */
export const ASSIGNMENT_MESSAGE_VARIABLES: MessageVariable[] = [
	...BUILT_IN_MESSAGE_VARIABLES,
	ASSIGNMENT_LINK_VARIABLE,
];

/** Id-shaped so the editors' looser `VariableItem` fits; defaulted to their own `?? MESSAGE_VARIABLES`. */
export const offersAssignmentLink = (variables: { id: string }[] = MESSAGE_VARIABLES): boolean =>
	variables.some((variable) => variable.id === ASSIGNMENT_LINK_VARIABLE.id);

export const INTERVIEW_NAME_VARIABLES: MessageVariable[] = [
	TEMPLATE_VARIABLES.jobName,
	TEMPLATE_VARIABLES.stageName,
	TEMPLATE_VARIABLES.candidateName,
	TEMPLATE_VARIABLES.workspaceName,
];
